/**
 * A record's activity timeline (PRD §5.8).
 *
 * "This is the memory layer." Ordered by when the event happened rather than
 * when it was typed, because backdating is normal — someone writes up
 * Tuesday's call on Thursday, and a timeline ordered by keystroke puts it in
 * the wrong place.
 *
 * Editing and deleting are offered only to the author or an admin. The server
 * enforces that (PRD §5.8); hiding the controls avoids offering an action that
 * would 403.
 *
 * Lives in `components/domain` rather than a feature because PRD §5.8 attaches
 * activities to startups, pilots, customers, assets and contacts alike — a
 * feature must not import another feature (plan §3.1), so shared domain UI is
 * promoted here instead.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.8, plan §3.1
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Activity } from '@nexus/contracts';

import {
  useCreateActivity,
  useDeleteActivity,
  useUpdateActivity,
} from '../../../lib/activities.queries';
import { useAuth } from '../../../lib/auth-context';
import { formatDate, formatDateTime } from '../../../lib/format';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Dialog } from '../../ui/Dialog';

import { isoToDateInput, type ActivityType } from './activity-form-schema';
import { ActivityForm } from './ActivityForm';

/** Tone per activity type, so a timeline is scannable. */
const TYPE_TONE: Record<ActivityType, 'ok' | 'info' | 'plan' | 'mute'> = {
  CALL: 'info',
  MEETING: 'info',
  EMAIL: 'mute',
  SITE_VISIT: 'ok',
  DEMO: 'plan',
  DOCUMENT_RECEIVED: 'mute',
  NOTE: 'mute',
};

export interface ActivityTimelineProps {
  /** Which record the entries attach to. Exactly one owner. */
  owner: { startupId: string } | { pilotId: string } | { customerId: string };
  activities: readonly Activity[];
  isPending: boolean;
  isError: boolean;
}

export function ActivityTimeline({
  owner,
  activities,
  isPending,
  isError,
}: ActivityTimelineProps): React.JSX.Element {
  const { t } = useTranslation();
  const { user, can } = useAuth();

  const createActivity = useCreateActivity();
  const updateActivity = useUpdateActivity();
  const deleteActivity = useDeleteActivity();

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Activity | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  /**
   * True when the caller may change this entry.
   *
   * PRD §5.8 calls this the memory layer, and a memory anyone can rewrite is
   * not one — but an admin has to be able to correct a record after someone
   * leaves.
   */
  const canChange = (activity: Activity): boolean =>
    can('EDITOR') && (activity.authorId === user?.id || can('ADMIN'));

  if (isPending) {
    return (
      <p role="status" className="text-body text-sm">
        {t('state.loading')}
      </p>
    );
  }

  if (isError) {
    return (
      <p role="alert" className="text-sem-bad text-sm">
        {t('errors.generic')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-body text-sm">{t('activity.intro')}</p>

      {can('EDITOR') ? (
        adding ? (
          <div className="border-gray-100 rounded-md border p-3">
            <ActivityForm
              submitLabel={t('activity.save')}
              submittingLabel={t('activity.saving')}
              errorMessage={t('activity.saveFailed')}
              onCancel={() => {
                setAdding(false);
              }}
              onSubmit={async (values) => {
                await createActivity.mutateAsync({
                  type: values.type,
                  occurredAt: values.occurredOn,
                  summary: values.summary,
                  ...owner,
                });
                setAdding(false);
              }}
            />
          </div>
        ) : (
          <div>
            <Button
              onClick={() => {
                setActionError(null);
                setAdding(true);
              }}
            >
              {t('activity.add')}
            </Button>
          </div>
        )
      ) : null}

      {actionError === null ? null : (
        <p role="alert" className="text-sem-bad text-sm">
          {actionError}
        </p>
      )}

      {activities.length === 0 ? (
        <p className="text-gray-400 text-sm">{t('activity.empty')}</p>
      ) : (
        <ol aria-label={t('activity.title')} className="flex flex-col gap-3">
          {activities.map((activity) => (
            <li
              key={activity.id}
              className="border-gray-100 flex flex-col gap-2 rounded-md border p-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={TYPE_TONE[activity.type]}>
                  {t(`activity.types.${activity.type}`)}
                </Badge>
                {/* A `time` element with a machine-readable value, so the date
                    is unambiguous to anything parsing the page. */}
                <time dateTime={activity.occurredAt} className="text-navy-text text-sm">
                  {formatDate(activity.occurredAt)}
                </time>
                <span className="text-gray-400 text-xs">
                  {activity.authorName ?? t('activity.unknownAuthor')}
                </span>
              </div>

              {editingId === activity.id ? (
                <ActivityForm
                  defaultValues={{
                    type: activity.type,
                    occurredOn: isoToDateInput(activity.occurredAt),
                    summary: activity.summary,
                  }}
                  submitLabel={t('activity.update')}
                  submittingLabel={t('activity.saving')}
                  errorMessage={t('activity.updateFailed')}
                  onCancel={() => {
                    setEditingId(null);
                  }}
                  onSubmit={async (values) => {
                    await updateActivity.mutateAsync({
                      id: activity.id,
                      type: values.type,
                      occurredAt: values.occurredOn,
                      summary: values.summary,
                    });
                    setEditingId(null);
                  }}
                />
              ) : (
                <>
                  <p className="text-body text-sm">{activity.summary}</p>

                  {canChange(activity) ? (
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setActionError(null);
                          setEditingId(activity.id);
                        }}
                      >
                        {t('activity.edit')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setActionError(null);
                          setPendingDelete(activity);
                        }}
                      >
                        {t('activity.delete')}
                      </Button>
                    </div>
                  ) : null}

                  {/* When it was TYPED, distinct from when it happened. Shown
                      only where the two differ, so a same-day entry is not
                      cluttered with a redundant line. */}
                  {formatDate(activity.createdAt) ===
                  formatDate(activity.occurredAt) ? null : (
                    <p className="text-gray-400 text-xs">
                      {t('activity.loggedOn', {
                        when: formatDateTime(activity.createdAt),
                      })}
                    </p>
                  )}
                </>
              )}
            </li>
          ))}
        </ol>
      )}

      {/* Deletion is confirmed: PRD §6.2 soft-deletes, but from the user's
          side the entry is gone and there is no undo in this interface. */}
      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          /* v8 ignore next -- Radix calls this with `true` only when the dialog
             opens itself, which it never does: `open` is driven entirely by
             `pendingDelete`. */
          if (!open) setPendingDelete(null);
        }}
        title={t('activity.confirmDelete')}
        description={t('activity.confirmDeleteBody')}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setPendingDelete(null);
              }}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                const target = pendingDelete;
                /* v8 ignore next -- the button only exists while
                   `pendingDelete` is set, so this is a type-level necessity
                   rather than a reachable branch. */
                if (target === null) return;

                setPendingDelete(null);
                deleteActivity.mutate(target.id, {
                  onError: () => {
                    setActionError(t('activity.deleteFailed'));
                  },
                });
              }}
            >
              {t('activity.delete')}
            </Button>
          </>
        }
      >
        <p className="text-body text-sm">{pendingDelete?.summary}</p>
      </Dialog>
    </div>
  );
}
